import { Button, IconButton, Icons, Stack, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { getGetPendingTasksQueryKey } from '../../../api/created/tasks.ts';
import { useMarkJournalAsDone } from '../../../api/generated/journal-controller/journal-controller.ts';
import { JournalDtoTopic, PatientBasicsDto, TaskDto, UserDto } from '../../../api/generated/model';
import { SubCard } from '../../../components/SubCard.tsx';
import { formatLocalDateTimeWithGermanMonthDateOnly } from '../../../utils/date.ts';
import { FileLink } from '../../components/FileLink.tsx';
import {
  CreateJournalEntryDialog,
  type EditableJournalEntryData,
} from '../../components/journal/CreateJournalEntryDialog.tsx';
import { NavigateToJournalTopicButton } from '../../components/journal/NavigateToJournalTopicButton.tsx';

function formatUserName(user?: UserDto) {
  if (!user) {
    return '';
  }

  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.username || '';
}

export function TaskJournalCard({ task, patient }: { task: TaskDto; patient: PatientBasicsDto }) {
  const queryClient = useQueryClient();
  const createdBy = task.journal?.createdBy;
  // Zuständigkeit is the assigned employee; when the note has none, the creator covers both rows.
  const createdByName = formatUserName(createdBy);
  const responsibleName = formatUserName(task.employee ?? createdBy);
  const patientName = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
  const noteTitle = task.notes || task.journal?.notes || 'Keine Beschreibung des To Dos eingetragen';
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const editableJournalEntry: EditableJournalEntryData | undefined = task.journal
    ? {
        id: task.journal.id,
        topic: task.journal.topic,
        notes: task.journal.notes,
        isTodo: true,
        due: task.due,
        assignee: task.employee ?? null,
        file: task.journal.file,
      }
    : undefined;

  const markJournalAsDoneMutation = useMarkJournalAsDone({
    mutation: {
      onSuccess: () => {
        toast.success('Notiz wurde erfolgreich als erledigt markiert');
        void queryClient.invalidateQueries({ queryKey: getGetPendingTasksQueryKey() });
      },
      onError: () => {
        toast.error('Beim markieren der Notiz als erledigt ist ein Fehler aufgetreten');
      },
    },
  });

  return (
    <Stack direction={'row'}>
      <Stack flex={1} gap={2}>
        <Stack direction={'row'} gap={2}>
          <SubCard>
            <Icons.Notes fontSize={'small'} />
          </SubCard>
          <Stack justifyContent={'space-between'}>
            <Typography sx={{ whiteSpace: 'pre-line' }} level={'title-lg'}>
              {noteTitle}
            </Typography>
            <Typography level={'body-sm'}>{formatLocalDateTimeWithGermanMonthDateOnly(task.due)}</Typography>
          </Stack>
        </Stack>

        <Typography sx={{ whiteSpace: 'pre-line' }} level={'title-sm'}>
          {patientName}
        </Typography>
        {task.journal?.file && <FileLink fileDetails={task.journal.file} />}
        {createdByName && <Typography level={'body-sm'}>Erfasst von: {createdByName}</Typography>}
        {responsibleName && <Typography level={'body-sm'}>Zuständigkeit: {responsibleName}</Typography>}
      </Stack>
      <Stack justifyContent={'space-between'}>
        <Stack direction={'row'} gap={1} justifyContent={'flex-end'}>
          {editableJournalEntry && (
            <IconButton
              color={'primary'}
              onClick={() => setIsEditDialogOpen(true)}
              title={'Notiz bearbeiten'}
              aria-label={'Notiz bearbeiten'}>
              <Icons.Edit />
            </IconButton>
          )}
          {task.journal && task.journal.topic !== JournalDtoTopic.NONE && (
            <NavigateToJournalTopicButton
              topic={task.journal.topic}
              patient={patient}
              screeningCaseId={task.referenceId}
            />
          )}
        </Stack>
        <Button
          startDecorator={<Icons.Check />}
          color={'success'}
          onClick={() =>
            task.journal &&
            markJournalAsDoneMutation.mutate({
              id: task.journal.id,
            })
          }>
          Erledigt
        </Button>
      </Stack>
      {editableJournalEntry && (
        <CreateJournalEntryDialog
          patientId={patient.id}
          preselectedTopic={editableJournalEntry.topic}
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          editableJournalEntry={editableJournalEntry}
          disabledTopics={[JournalDtoTopic.TRACING_CONTACT]}
        />
      )}
    </Stack>
  );
}
